import { useContext, useEffect, useRef, useState } from 'react';
import { produce } from 'immer';
import { ApiContext } from '@api/api';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  CGV2LocationOutDTO,
  EntityTypeEnum,
  MultipleExpeditionDetailV4OutDTO,
  ResponseDeadlineModeEnum,
  SnRouteConsolidationGroupOutDTO,
  SupplierNotificationToExpeditionOutDTO,
} from '@api/ringil-api.v2.tsx';
import { NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { newExpeditionInitialData, newExpeditionInitialDataWithDefaults } from '@app/pages/expedition/create/data.ts';
import { applyExpeditionDefaults } from '@app/pages/expedition/create/defaults.ts';
import { DeadlineMode, ExpeditionDetailForPrefill, mapExpeditionDetailToForm } from '@app/pages/expedition/create/mappers/detailToForm.ts';
import { mapSupplierNotificationToExpeditionDetail } from '@app/pages/expedition/create/mappers/supplierNotificationToDetail.ts';
import { MergeConflict, mergeExpeditionsToForm, MergeOptions, MergeResult } from '@app/pages/expedition/create/mappers/mergeToForm.ts';
import { consolidationRouteBuilder } from '@app/pages/expedition/create/routeConsolidation.ts';
import { MergeSource } from '@app/pages/expedition/create/mergeEligibility.ts';
import { CarrierDistributionSelectorData } from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { PrefillOptions } from '@components/RingilComponents/expeditionPrefillOptions.ts';
import { isNotNullOrUndefined } from '@utils/utils.tsx';
import { useLovTruckTypes } from '@hooks/customer.hooks.tsx';

/**
 * Everything the create-shipment form needs to be filled from an existing entity.
 *
 * The form has three prefill sources (copy a shipment, copy a template, create from a supplier
 * notification) that all need the same three things to happen in the same order: read the company's
 * distribution settings, load the source plus the current address-book data for its stops, then hand
 * the finished form model over in one piece. This hook is that sequence; callers await one promise
 * instead of choreographing effects.
 */

export interface DistributionSettings {
  distributionHours: number;
  deadlineMode: DeadlineMode;
}

export type PrefillSource =
  | { kind: 'expedition'; id: string; options?: PrefillOptions }
  | { kind: 'template'; id: string; options?: PrefillOptions }
  | { kind: 'supplierNotification'; id: string; options?: PrefillOptions }
  /** Several entities folded into one form. Either list may be empty, but not both. */
  | { kind: 'merge'; expeditionIds: string[]; supplierNotificationIds: string[] }
  /** A route-optimization group: merged like the notifications it holds, but on the route the optimizer computed. */
  | {
      kind: 'consolidation';
      group: SnRouteConsolidationGroupOutDTO;
      consolidationRequestId: string;
      modelVersion?: string;
      /** Taken out of the optimized group on the list by removing locations; `group` is already without them. */
      removedSupplierNotificationIds?: string[];
    };

/** The optimization a merged form came from; sent back with the save for the notifications' audit. */
export interface ConsolidationOrigin {
  requestId: string;
  groupId: number;
  modelVersion?: string;
  kmSavings: number;
  /** How many notifications the user removed from the group; the optimizer's saving no longer applies then. */
  removedCount: number;
}

export interface PrefillResult {
  values: NewExpeditionForm;
  /** The distribution that was pre-filled, for the carrier selector to diff against. */
  originalDistribution?: CarrierDistributionSelectorData;
  /** Set only for a merge: the shipments being replaced, and what could not be reconciled. */
  merge?: {
    sources: MergeSource[];
    conflicts: MergeConflict[];
    keptApart: MergeResult['keptApart'];
    contactConflicts: MergeResult['contactConflicts'];
    timeslotConflicts: MergeResult['timeslotConflicts'];
    summary: MergeResult['summary'];
    /** True when any source has a stop with a reservation on it. */
    hasLinkedReservations: boolean;
    /** True when any source was created from a supplier notification. */
    hasLinkedSupplierNotifications: boolean;
    /** Set when the merge is a route-optimization group. */
    consolidation?: ConsolidationOrigin;
  };
}

const DEFAULT_PREFILL_OPTIONS: PrefillOptions = {
  routeAndParameters: true,
  cargoAndOrders: true,
  documents: true,
  labels: true,
  internalInfo: true,
  carrierSelection: true,
  carrierNote: true,
  linkToSN: false,
  copySNAttachments: false,
};

const ENTITY_TYPE_BY_KIND: Record<PrefillSource['kind'], EntityTypeEnum> = {
  expedition: EntityTypeEnum.EXPEDITION,
  template: EntityTypeEnum.EXPEDITION_TEMPLATE,
  supplierNotification: EntityTypeEnum.SUPPLIER_NOTIFICATION,
  merge: EntityTypeEnum.EXPEDITION,
  consolidation: EntityTypeEnum.EXPEDITION,
};

/**
 * Gives every stop after the first an explicit list of the orders it unloads, defaulting to
 * everything loaded before it. Stops arriving without one would otherwise offer no unloading at all.
 */
export const normalizeUnloadingOrderIds = (values: NewExpeditionForm): NewExpeditionForm =>
  produce(values, draft => {
    draft.locations?.forEach((loc, index) => {
      if (index === 0) return;
      if (loc.unloadingOrderIds !== null && loc.unloadingOrderIds !== undefined) return;

      const loadedBefore = draft.locations
        .slice(0, index)
        .map(previous => previous.orders || [])
        .flat()
        .filter(order => order != null)
        .map(order => order.id);

      if (loadedBefore.length > 0) {
        loc.unloadingOrderIds = [...loadedBefore];
      }
    });
  });

const mapDocument = (doc: any) => ({
  attachmentId: doc.attachmentId,
  fileName: doc.fileName,
  size: doc.size,
  contentType: doc.contentType,
  userId: doc.userId,
  userName: doc.userName,
  companyId: doc.companyId,
  uploadedAt: doc.uploadedAt,
  companyName: doc.companyName,
  accessType: doc.accessType,
  accessTypeName: doc.accessTypeName,
  documentType: doc.documentType,
  documentTypeName: doc.documentTypeName ?? doc.documentType,
  canBeDeleted: doc.canBeDeleted,
  userNote: doc.userNote,
  userDateLocalDate: doc.userDateLocalDate,
});

interface UseExpeditionPrefillProps {
  editDistributionAllowed: boolean;
}

export const useExpeditionPrefill = ({ editDistributionAllowed }: UseExpeditionPrefillProps) => {
  const {
    getDistributionSettings,
    getExpeditionV4Detail,
    getTemplateToExpeditionDetail,
    cuGetSupplierNotificationForExpedition,
    cuGetMultipleSupplierNotificationsForExpedition,
    getOneCGLocationV2,
    copyOfAttachmentsFromEntity,
    getMultipleExpeditionDetails,
  } = useContext(ApiContext);
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { showToastMessage } = useContext(ToastContext);
  const { tr } = useTranslationLgs();
  // The page already shows this codebook in the truck-type tag; the shared hook hands over the copy
  // it has rather than fetching it again, and a merge can then name the truck type instead of coding it.
  const truckTypes = useLovTruckTypes();

  const [settings, setSettings] = useState<DistributionSettings>({ distributionHours: 8, deadlineMode: 'relative' });
  const settingsPromiseRef = useRef<Promise<DistributionSettings>>(null);

  /** Loaded once and shared: several prefills in one session must not re-request the settings. */
  const loadSettings = (): Promise<DistributionSettings> => {
    if (!settingsPromiseRef.current) {
      settingsPromiseRef.current = new Promise<DistributionSettings>(resolve => {
        getDistributionSettings(d => {
          const loaded: DistributionSettings = {
            distributionHours: d.defaultHoursInFuture,
            deadlineMode: d.responseDeadlineMode === ResponseDeadlineModeEnum.ABSOLUTE ? 'absolute' : 'relative',
          };
          setSettings(loaded);
          resolve(loaded);
        });
      });
    }
    return settingsPromiseRef.current;
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSourceDetail = (source: PrefillSource): Promise<ExpeditionDetailForPrefill | null> =>
    new Promise(resolve => {
      const callbacks = {
        onError: (error: unknown) => {
          console.error('Failed to load the entity to prefill from:', error);
          resolve(null);
        },
      };

      switch (source.kind) {
        case 'expedition':
          return getExpeditionV4Detail(source.id, detail => resolve(detail), null, callbacks);
        case 'template':
          return getTemplateToExpeditionDetail(source.id, detail => resolve(detail), null, callbacks);
        case 'supplierNotification':
          return cuGetSupplierNotificationForExpedition(source.id, notification => resolve(mapSupplierNotificationToExpeditionDetail(notification)), null, callbacks);
      }
    });

  const loadOneLocation = (locationId: string): Promise<CGV2LocationOutDTO | null> =>
    new Promise(resolve => {
      getOneCGLocationV2(locationId, data => resolve(data ?? null), null, {
        onEntityNotFound: () => resolve(null),
        onError: () => resolve(null),
      });
    });

  /** Current address-book data for every stop that points at one; a stop whose location cannot be
   *  loaded falls back to the snapshot stored on the source shipment.
   *
   *  A stop whose location has since been superseded is resolved to its **replacement**, stored under
   *  the original id so the mappers still find it. This is the one place that may follow the forward:
   *  the lookup is only ever used to build a *new* shipment, while an existing one must keep showing
   *  the location it was actually created on. One hop is always enough — a replacement never carries a
   *  `supersededBy` of its own — and a replacement that cannot be loaded falls back to the original. */
  const loadLocationData = async (detail: ExpeditionDetailForPrefill): Promise<Map<string, CGV2LocationOutDTO>> => {
    const locationDataByLocationId = new Map<string, CGV2LocationOutDTO>();

    await Promise.all(
      detail.locations
        .filter(loc => loc.companyLocationId)
        .map(async loc => {
          const data = await loadOneLocation(loc.companyLocationId);
          if (!data) return;
          const replacement = data.supersededBy ? await loadOneLocation(data.supersededBy.id) : null;
          locationDataByLocationId.set(loc.companyLocationId, replacement ?? data);
        }),
    );

    return locationDataByLocationId;
  };

  const loadAttachments = (entityType: EntityTypeEnum, id: string, asPrivate: boolean = true): Promise<any[]> =>
    new Promise(resolve => {
      copyOfAttachmentsFromEntity(entityType, id, asPrivate, documents => {
        resolve((documents || []).map(mapDocument));
      });
    });

  const loadAllAttachments = async (detail: ExpeditionDetailForPrefill, options: PrefillOptions, entityType: EntityTypeEnum): Promise<any[]> => {
    const expeditionDocs =
      options.documents && entityType !== EntityTypeEnum.SUPPLIER_NOTIFICATION ? loadAttachments(EntityTypeEnum.EXPEDITION, detail.id) : Promise.resolve([]);

    const snId = entityType === EntityTypeEnum.SUPPLIER_NOTIFICATION ? detail.id : detail.supplierNotificationLink?.supplierNotificationId;
    const snDocs = options.copySNAttachments && snId ? loadAttachments(EntityTypeEnum.SUPPLIER_NOTIFICATION, snId, options.markAsPrivate) : Promise.resolve([]);

    const [expDocs, notificationDocs] = await Promise.all([expeditionDocs, snDocs]);
    return [...(expDocs || []), ...(notificationDocs || [])];
  };

  /**
   * Builds the form model for an already-loaded source. Kept separate from {@link prefill} so
   * callers holding a detail of their own (the favourites sidebar) can reuse the same pipeline.
   */
  const buildValuesFromDetail = async (
    detail: ExpeditionDetailForPrefill,
    options: PrefillOptions,
    entityType: EntityTypeEnum,
    deadlineMode?: DeadlineMode,
  ): Promise<PrefillResult> => {
    const resolvedSettings = await loadSettings();

    if (!options.routeAndParameters) {
      return { values: newExpeditionInitialDataWithDefaults() };
    }

    const locationDataByLocationId = await loadLocationData(detail);

    let originalDistribution: CarrierDistributionSelectorData | undefined;
    const values = produce(newExpeditionInitialData(), draft => {
      originalDistribution = mapExpeditionDetailToForm(detail, draft, {
        prefillOptions: options,
        effectiveHours: resolvedSettings.distributionHours,
        defaultCurrencyIsoCode: loggedUser.defaultCurrencyIsoCode,
        defaultTimezoneId: loggedUser.defaultTimezoneId,
        deadlineMode: deadlineMode ?? resolvedSettings.deadlineMode,
        editDistributionAllowed,
        locationDataByLocationId,
      });
    });

    const attachments = await loadAllAttachments(detail, options, entityType);

    // A parameter the source never answered falls back to the default rather than being left for the
    // backend to fill in silently — the same value a blank form would have started with.
    return {
      values: applyExpeditionDefaults(normalizeUnloadingOrderIds(attachments.length > 0 ? { ...values, attachments } : values)),
      originalDistribution,
    };
  };

  const showPrefilledToast = (entityType: EntityTypeEnum) => {
    showToastMessage(
      tr('CreateExpedition.prefillSuccess', 'Předvyplnění dokončeno'),
      tr('CreateExpedition.prefillSuccessMessage.' + entityType, 'Data byla úspěšně předvyplněna z oblíbené ' + entityType),
      'success',
    );
  };

  /**
   * Loads every entity being merged and folds them into one form.
   *
   * Supplier notifications go through the same adapter the "create shipment from notification" flow
   * uses, so by the time the merge sees them they are indistinguishable from shipments — which is
   * what lets one set of merge rules cover shipments, notifications and a mix of the two. The
   * address-book lookups run for all of them up front, so each source is mapped against current
   * location data exactly as a single copy would be.
   */
  const buildMergedValues = async (expeditionIds: string[], supplierNotificationIds: string[], mergeOptions?: MergeOptions): Promise<PrefillResult | null> => {
    const resolvedSettings = await loadSettings();

    const expeditionDetails =
      expeditionIds.length === 0
        ? []
        : await new Promise<MultipleExpeditionDetailV4OutDTO[] | null>(resolve => {
            getMultipleExpeditionDetails(expeditionIds, data => resolve(data), null, {
              onError: error => {
                console.error('Failed to load the shipments to merge:', error);
                resolve(null);
              },
            });
          });
    if (expeditionDetails === null) return null;

    const notifications =
      supplierNotificationIds.length === 0
        ? []
        : await new Promise<SupplierNotificationToExpeditionOutDTO[] | null>(resolve => {
            cuGetMultipleSupplierNotificationsForExpedition(supplierNotificationIds, data => resolve(data), null, {
              onError: error => {
                console.error('Failed to load the supplier notifications to merge:', error);
                resolve(null);
              },
            });
          });
    if (notifications === null) return null;

    // Keep the order the user selected them in; neither endpoint is obliged to. Shipments come
    // first so that a mixed merge starts from the entities that already carry a carrier and a price.
    const ordered: { source: MergeSource; detail: ExpeditionDetailForPrefill }[] = [
      ...expeditionIds
        .map(id => expeditionDetails.find(detail => detail.id === id))
        .filter(Boolean)
        .map(detail => ({ source: { kind: 'expedition' as const, id: detail.id, applicationId: detail.applicationId }, detail })),
      ...supplierNotificationIds
        .map(id => notifications.find(notification => notification.supplierNotificationFromId === id))
        .filter(Boolean)
        .map(notification => ({
          source: { kind: 'supplierNotification' as const, id: notification.supplierNotificationFromId, applicationId: notification.applicationId },
          detail: mapSupplierNotificationToExpeditionDetail(notification),
        })),
    ];
    if (ordered.length === 0) return null;

    const locationDataByLocationId = new Map<string, CGV2LocationOutDTO>();
    for (const { detail } of ordered) {
      const loaded = await loadLocationData(detail);
      loaded.forEach((value, key) => locationDataByLocationId.set(key, value));
    }

    const { values, conflicts, keptApart, contactConflicts, timeslotConflicts, summary } = mergeExpeditionsToForm(
      ordered.map(entry => entry.detail),
      {
        prefillOptions: DEFAULT_PREFILL_OPTIONS,
        effectiveHours: resolvedSettings.distributionHours,
        defaultCurrencyIsoCode: loggedUser.defaultCurrencyIsoCode,
        defaultTimezoneId: loggedUser.defaultTimezoneId,
        deadlineMode: resolvedSettings.deadlineMode,
        editDistributionAllowed,
        locationDataByLocationId,
      },
      { tr, truckTypeName: code => truckTypes.find(type => type.code === code)?.name },
      mergeOptions,
    );

    const attachments = (
      await Promise.all(
        ordered.map(({ source, detail }) => loadAttachments(source.kind === 'expedition' ? EntityTypeEnum.EXPEDITION : EntityTypeEnum.SUPPLIER_NOTIFICATION, detail.id)),
      )
    ).flat();
    const attachmentsById = new Map(attachments.map(attachment => [attachment.attachmentId, attachment]));

    return {
      values: { ...values, attachments: Array.from(attachmentsById.values()) },
      originalDistribution: values.distribution,
      merge: {
        sources: ordered.map(entry => entry.source),
        conflicts,
        keptApart,
        contactConflicts,
        timeslotConflicts,
        summary,
        hasLinkedReservations: ordered.some(({ detail }) => detail.locations?.some(location => isNotNullOrUndefined(location.linkedReservation))),
        hasLinkedSupplierNotifications: ordered.some(({ detail }) => isNotNullOrUndefined(detail.supplierNotificationLink)),
      },
    };
  };

  /** Loads `source` and returns the finished form model, or null when the source could not be read. */
  const prefill = async (source: PrefillSource, deadlineMode?: DeadlineMode): Promise<PrefillResult | null> => {
    await loadSettings();

    if (source.kind === 'merge') {
      const result = await buildMergedValues(source.expeditionIds ?? [], source.supplierNotificationIds ?? []);
      if (result) showPrefilledToast(EntityTypeEnum.EXPEDITION);
      return result;
    }

    if (source.kind === 'consolidation') {
      const { group } = source;
      // Everything but the route is the ordinary notification merge. A notification the backend no
      // longer returns drops out of the merge; the route builder then skips its stops.
      const result = await buildMergedValues([], group.supplierNotificationIds, {
        buildRoute: consolidationRouteBuilder(group.routeStops),
        truckTypeCode: group.truckTypeCode,
      });
      if (!result) return null;
      showPrefilledToast(EntityTypeEnum.EXPEDITION);
      return {
        ...result,
        merge: {
          ...result.merge,
          consolidation: {
            requestId: source.consolidationRequestId,
            groupId: group.groupId,
            modelVersion: source.modelVersion,
            kmSavings: group.kmSavings,
            removedCount: source.removedSupplierNotificationIds?.length ?? 0,
          },
        },
      };
    }

    const detail = await loadSourceDetail(source);
    if (!detail) return null;

    const entityType = ENTITY_TYPE_BY_KIND[source.kind];
    const result = await buildValuesFromDetail(detail, source.options ?? DEFAULT_PREFILL_OPTIONS, entityType, deadlineMode);
    showPrefilledToast(entityType);
    return result;
  };

  return { settings, prefill };
};
